import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Btn, Input, Textarea, Card } from "@/components/ui-kit";
import { Save, Building2, CreditCard, Receipt as ReceiptIcon } from "lucide-react";

const empty = {
  legal_name_en: "", legal_name_ar: "", trade_name: "",
  tax_registration_number: "", commercial_registration_number: "", activity_code: "",
  address_line1: "", address_line2: "", governorate: "", country: "Egypt", postal_code: "",
  phone: "", email: "", website: "", logo_url: "",
  default_vat_rate: 14.0, default_payment_terms: "Net 30",
  bank_name: "", bank_account: "", bank_iban: "",
  invoice_footer_note_en: "", invoice_footer_note_ar: "",
};

export default function CompanySettings() {
  const { user } = useAuth();
  const canEdit = user?.permissions?.includes("settings.company.manage");
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/company");
        setForm({ ...empty, ...data });
      } catch (err) {
        console.error("Load company settings failed", err);
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setSaved(false);
    try {
      const payload = { ...form, default_vat_rate: parseFloat(form.default_vat_rate) || 14 };
      await api.put("/settings/company", payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      alert("Save failed: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  const fld = (props) => ({
    ...props,
    value: form[props.name] ?? "",
    onChange: (e) => setForm({ ...form, [props.name]: e.target.value }),
    disabled: !canEdit,
  });

  return (
    <div data-testid="company-settings-page">
      <PageHeader
        title="Company Settings"
        subtitle="Legal info shown on every quotation and tax invoice"
      />

      {!canEdit && (
        <div className="mb-4 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          You have read-only access. Contact an administrator to edit company info.
        </div>
      )}

      <form onSubmit={submit} className="space-y-6" data-testid="company-form">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-blue-700" />
            <h2 className="font-bold text-lg">Identity</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Legal Name (English) *" {...fld({ name: "legal_name_en" })} required={canEdit} data-testid="company-legal-en" />
            <Input label="Legal Name (Arabic) *" {...fld({ name: "legal_name_ar" })} dir="rtl" required={canEdit} data-testid="company-legal-ar" />
            <Input label="Trade Name" {...fld({ name: "trade_name" })} />
            <Input label="Activity Code (ETA)" {...fld({ name: "activity_code" })} placeholder="e.g. 4646" />
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <ReceiptIcon className="w-5 h-5 text-blue-700" />
            <h2 className="font-bold text-lg">Tax Registration</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="Tax Registration # (الرقم الضريبي) *" {...fld({ name: "tax_registration_number" })} required={canEdit} data-testid="company-tax-id" />
            <Input label="Commercial Registration # (السجل التجاري) *" {...fld({ name: "commercial_registration_number" })} required={canEdit} data-testid="company-commercial-reg" />
            <Input label="Default VAT Rate (%)" type="number" min="0" step="0.01" {...fld({ name: "default_vat_rate" })} />
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="font-bold text-lg mb-4">Address & Contact</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Address Line 1 *" {...fld({ name: "address_line1" })} required={canEdit} />
            <Input label="Address Line 2" {...fld({ name: "address_line2" })} />
            <Input label="Governorate" {...fld({ name: "governorate" })} placeholder="e.g. Gharbia" />
            <Input label="Country" {...fld({ name: "country" })} />
            <Input label="Postal Code" {...fld({ name: "postal_code" })} />
            <Input label="Phone *" {...fld({ name: "phone" })} required={canEdit} data-testid="company-phone" />
            <Input label="Email *" type="email" {...fld({ name: "email" })} required={canEdit} data-testid="company-email" />
            <Input label="Website" {...fld({ name: "website" })} />
            <Input label="Logo URL" {...fld({ name: "logo_url" })} placeholder="https://..." />
            <Input label="Default Payment Terms" {...fld({ name: "default_payment_terms" })} placeholder="e.g. Net 30" />
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5 text-blue-700" />
            <h2 className="font-bold text-lg">Banking (printed on invoices)</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="Bank Name" {...fld({ name: "bank_name" })} />
            <Input label="Account Number" {...fld({ name: "bank_account" })} />
            <Input label="IBAN" {...fld({ name: "bank_iban" })} />
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="font-bold text-lg mb-4">Invoice Footer Notes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Textarea label="Footer Note (English)" rows={3} {...fld({ name: "invoice_footer_note_en" })} placeholder="Thank you for your business..." />
            <Textarea label="Footer Note (Arabic)" rows={3} {...fld({ name: "invoice_footer_note_ar" })} dir="rtl" />
          </div>
        </Card>

        {canEdit && (
          <div className="flex items-center justify-end gap-3 pt-2">
            {saved && <span className="text-sm text-emerald-600 font-medium">✓ Saved</span>}
            <Btn type="submit" disabled={busy} data-testid="company-save-btn">
              <Save className="w-4 h-4" /> {busy ? "Saving..." : "Save Settings"}
            </Btn>
          </div>
        )}
      </form>
    </div>
  );
}
